import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useSubscriptions } from './SubscriptionContext';
import { 
  getServiceProviders, 
  getThreadsByService,
  getNotificationsByThread,
  updateNotificationReadStatus,
  markAllAsRead,
  markServiceAsRead,
  getTotalUnreadCount,
  getCityUnreadCount,
  getBusinessUnreadCount,
  getFilteredNotifications
} from '../data/notificationsData';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const { isSubscribedToCity, isSubscribedToCityCategory } = useSubscriptions();
  const [services, setServices] = useState([]);
  const [currentService, setCurrentService] = useState(null);
  const [currentThreads, setCurrentThreads] = useState([]);
  const [currentThread, setCurrentThread] = useState(null);
  const [threadNotifications, setThreadNotifications] = useState([]);
  const [cityNotifications, setCityNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [showSettings, setShowSettings] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({
    total: 0,
    city: 0,
    business: 0
  });
  
  // Update unread counts
  const updateUnreadCounts = useCallback(() => {
    setUnreadCounts({
      total: getTotalUnreadCount(isSubscribedToCity, isSubscribedToCityCategory),
      city: getCityUnreadCount(isSubscribedToCity, isSubscribedToCityCategory),
      business: getBusinessUnreadCount(isSubscribedToCity, isSubscribedToCityCategory)
    });
  }, [isSubscribedToCity, isSubscribedToCityCategory]);
  
  // Update services list
  const updateServices = useCallback(() => {
    const updatedServices = getServiceProviders(isSubscribedToCity, isSubscribedToCityCategory);
    setServices(updatedServices);
  }, [isSubscribedToCity, isSubscribedToCityCategory]);
  
  // Initialize data and update when subscriptions change
  useEffect(() => {
    updateServices();
    updateUnreadCounts();
  }, [updateServices, updateUnreadCounts]);
  
  // Update threads or city notifications when service changes
  useEffect(() => {
    updateServices(); // Always refresh services when navigation changes
    
    if (currentService) {
      // Check if it's a city service
      if (currentService.startsWith('[City] ')) {
        // Get city notifications without grouping
        const cityName = currentService.replace('[City] ', '');
        const notifications = getFilteredNotifications(isSubscribedToCity, isSubscribedToCityCategory)
          .filter(n => n.source === 'city' && n.cityName === cityName)
          .sort((a, b) => {
            // Sort by date and time (newest first)
            const dateTimeA = new Date(a.date + 'T' + a.time);
            const dateTimeB = new Date(b.date + 'T' + b.time);
            return dateTimeB - dateTimeA;
          });
        setCityNotifications(notifications);
        setCurrentThreads([]);
      } else {
        // Regular business service - use threading
        const threads = getThreadsByService(currentService, isSubscribedToCity, isSubscribedToCityCategory);
        setCurrentThreads(threads);
        setCityNotifications([]);
      }
    } else {
      setCurrentThreads([]);
      setCityNotifications([]);
    }
    setCurrentThread(null);
    setThreadNotifications([]);
    updateUnreadCounts(); // Update counts when service changes
  }, [currentService, isSubscribedToCity, isSubscribedToCityCategory, updateServices, updateUnreadCounts]);
  
  // Update notifications when thread changes
  useEffect(() => {
    if (currentThread) {
      const notifications = getNotificationsByThread(currentThread, isSubscribedToCity, isSubscribedToCityCategory);
      setThreadNotifications(notifications);
      
      // Don't automatically mark thread as read when viewed
      // markThreadAsRead(currentThread);
      updateServices();
      updateUnreadCounts();
    } else {
      setThreadNotifications([]);
    }
  }, [currentThread, isSubscribedToCity, isSubscribedToCityCategory, updateServices, updateUnreadCounts]);

  // Filter services based on active tab
  const filteredServices = () => {
    if (activeTab === 'home') {
      return services;
    } else if (activeTab === 'cities') {
      return services.filter(service => service.source === 'city');
    } else if (activeTab === 'business') {
      return services.filter(service => service.source === 'business');
    }
    return services;
  };
  
  // Mark individual notification as read
  const markAsRead = useCallback((notificationId) => {
    updateNotificationReadStatus(notificationId, true);
    
    // Update services and counts immediately
    updateServices();
    updateUnreadCounts();
    
    // Update UI
    if (currentThread) {
      const notifications = getNotificationsByThread(currentThread, isSubscribedToCity, isSubscribedToCityCategory);
      setThreadNotifications(notifications);
    } else if (currentService && currentService.startsWith('[City] ')) {
      // Update city notifications
      const cityName = currentService.replace('[City] ', '');
      const notifications = getFilteredNotifications(isSubscribedToCity, isSubscribedToCityCategory)
        .filter(n => n.source === 'city' && n.cityName === cityName)
        .sort((a, b) => {
          const dateTimeA = new Date(a.date + 'T' + a.time);
          const dateTimeB = new Date(b.date + 'T' + b.time);
          return dateTimeB - dateTimeA;
        });
      setCityNotifications(notifications);
    }
    
    // If in a thread, also update the current threads list
    if (currentThread && currentService) {
      const threads = getThreadsByService(currentService, isSubscribedToCity, isSubscribedToCityCategory);
      setCurrentThreads(threads);
    }
  }, [currentThread, currentService, isSubscribedToCity, isSubscribedToCityCategory, updateServices, updateUnreadCounts]);
  
  // Mark all notifications as read
  const markAllNotificationsAsRead = useCallback(() => {
    markAllAsRead();
    updateServices();
    updateUnreadCounts();
    
    // Update current view if needed
    if (currentThread) {
      const notifications = getNotificationsByThread(currentThread, isSubscribedToCity, isSubscribedToCityCategory);
      setThreadNotifications(notifications);
    } else if (currentService && currentService.startsWith('[City] ')) {
      const cityName = currentService.replace('[City] ', '');
      const notifications = getFilteredNotifications(isSubscribedToCity, isSubscribedToCityCategory)
        .filter(n => n.source === 'city' && n.cityName === cityName)
        .sort((a, b) => {
          const dateTimeA = new Date(a.date + 'T' + a.time);
          const dateTimeB = new Date(b.date + 'T' + b.time);
          return dateTimeB - dateTimeA;
        });
      setCityNotifications(notifications);
    }
  }, [currentThread, currentService, isSubscribedToCity, isSubscribedToCityCategory, updateServices, updateUnreadCounts]);
  
  // Mark all notifications from current service as read
  const markCurrentServiceAsRead = useCallback(() => {
    if (currentService) {
      markServiceAsRead(currentService);
      updateServices();
      updateUnreadCounts();
      
      // Update view
      if (currentService.startsWith('[City] ')) {
        const cityName = currentService.replace('[City] ', '');
        const notifications = getFilteredNotifications(isSubscribedToCity, isSubscribedToCityCategory)
          .filter(n => n.source === 'city' && n.cityName === cityName)
          .sort((a, b) => {
            const dateTimeA = new Date(a.date + 'T' + a.time);
            const dateTimeB = new Date(b.date + 'T' + b.time);
            return dateTimeB - dateTimeA;
          });
        setCityNotifications(notifications);
      } else {
        const threads = getThreadsByService(currentService, isSubscribedToCity, isSubscribedToCityCategory);
        setCurrentThreads(threads);
      }
    }
  }, [currentService, isSubscribedToCity, isSubscribedToCityCategory, updateServices, updateUnreadCounts]);
  
  // Select a service
  const selectService = useCallback((serviceName) => {
    setCurrentService(serviceName);
  }, []);
  
  // Select a thread
  const selectThread = useCallback((threadId) => {
    setCurrentThread(threadId);
  }, []);
  
  // Navigate back
  const goBack = useCallback(() => {
    if (currentThread) {
      setCurrentThread(null);
      // Refresh services and counts when going back from thread
      updateServices();
      updateUnreadCounts();
    } else if (currentService) {
      setCurrentService(null);
      // Refresh services and counts when going back to main screen
      updateServices();
      updateUnreadCounts();
    }
  }, [currentThread, currentService, updateServices, updateUnreadCounts]);

  // Change active tab
  const changeTab = useCallback((tab) => {
    setActiveTab(tab);
    setCurrentService(null);
    setCurrentThread(null);
  }, []);
  
  return (
    <NotificationContext.Provider
      value={{
        services: filteredServices(),
        currentService,
        currentThreads,
        currentThread,
        threadNotifications,
        cityNotifications,
        activeTab,
        unreadCounts,
        showSettings,
        setShowSettings,
        selectService,
        selectThread,
        goBack,
        markAsRead,
        markAllNotificationsAsRead,
        markCurrentServiceAsRead,
        changeTab
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
